import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';

/**
 * Создание черновика платежа.
 *
 *   1. Ученик (селект с поиском)
 *   2. Учебный год (по умолчанию текущий, если на него есть привязанный к 1С
 *      договор; иначе ближайший будущий с таким договором)
 *   3. Договоры на этот год — авто-привязка, не выбираются вручную:
 *        • основной (school/kindergarten)
 *        • доп. услуги (paid_services)
 *      Платёж можно создать ТОЛЬКО если хотя бы один из договоров
 *      привязан к 1С — без этого касса/1С-выгрузка не сработает.
 *   4. Услуга (через optgroup):
 *        Основная услуга:
 *          • Ежемесячная оплата (школа/сад) — берётся из тарифа
 *          • Вступительный взнос (пока есть неоплаченный остаток; при
 *            частичной оплате предлагается доплата на остаток)
 *        Дополнительные занятия:
 *          • заглушка — список появится позже
 *      Тип позиции, название, цена и agreement_id выводятся из выбранной услуги.
 *   5. Канал, плательщик, описание.
 */
// «Модулькасса» — один пункт вместо «Наличные» / «Карта/СБП»: на самой кассе
// администратор уже выберет налик или карту, и в платеже это запишется в
// onec_document_kind. В фильтре списка платежей мы уже учитываем модулькассу
// при выборе «Наличные» / «Карта/СБП».
//
// «Банковский перевод» в этом списке намеренно отсутствует: такие платежи
// всегда подтягиваются импортом из 1С, вручную их не заводим.
const PROVIDERS = [
  { v: 'modulkassa',      label: 'Модулькасса' },
  { v: 'yookassa',        label: 'ЮКасса' },
  { v: 'onec_correction', label: 'Корректировка 1С' },
];

const AGREEMENT_TYPE_LABELS = {
  school:        'Школа',
  kindergarten:  'Детский сад',
  paid_services: 'Доп. услуги',
};

function fullName(s) {
  if (!s) return '';
  return [s.familyName, s.firstName, s.midName].filter(Boolean).join(' ');
}
function fmtAmount(v) {
  if (v === null || v === undefined || v === '') return '';
  return Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽';
}
function randomKey() {
  // Только для React-key, без претензий на крипто-безопасность.
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

/* ------------------------------------------------------------------ */
/* Селект ученика: кнопка → раскрывающийся список с поиском            */
/* ------------------------------------------------------------------ */
function StudentSelect({ value, onChange, disabled }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const [rows, setRows]     = useState([]);
  const [loading, setLoad]  = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoad(true);
    const t = setTimeout(() => {
      const qs = new URLSearchParams({
        per_page: '300', status: 'active', sort: 'name', dir: 'asc',
      });
      if (search.trim()) qs.set('search', search.trim());
      api.get('/api/students?' + qs.toString())
        .then((res) => { if (!cancelled) setRows(res.rows || []); })
        .catch(() => { if (!cancelled) setRows([]); })
        .finally(() => { if (!cancelled) setLoad(false); });
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, search]);

  return (
    <div className="onec-picker" ref={rootRef}>
      <div className="onec-picker__row">
        <button
          type="button"
          className="onec-picker__current"
          onClick={() => !disabled && setOpen((v) => !v)}
          disabled={disabled}
        >
          <span className="onec-picker__value">
            {value ? (
              <>
                <span className="onec-picker__name">{fullName(value)}</span>
                <span className="onec-picker__code">ID {value.id}</span>
              </>
            ) : (
              <span className="onec-picker__empty">Выберите ученика…</span>
            )}
          </span>
          <span style={{ display: 'inline-flex', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 120ms' }}>
            <Icon name="chevronDown" size={12} />
          </span>
        </button>
        {value && !disabled && (
          <button
            type="button"
            className="onec-picker__clear"
            onClick={() => { onChange(null); setSearch(''); }}
            title="Очистить"
          >
            <Icon name="x" size={12} />
          </button>
        )}
      </div>

      {open && (
        <div className="onec-picker__menu">
          <input
            className="onec-picker__search"
            placeholder="Поиск по ФИО, телефону или ФИО родителя…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="onec-picker__list">
            {loading && rows.length === 0 && (
              <div className="onec-picker__hint">Загрузка…</div>
            )}
            {!loading && rows.length === 0 && (
              <div className="onec-picker__hint">Никого не найдено</div>
            )}
            {rows.map((s) => (
              <button
                key={s.id}
                type="button"
                className="onec-picker__item"
                onClick={() => { onChange(s); setOpen(false); setSearch(''); }}
                style={{ gridTemplateColumns: '1fr auto' }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {fullName(s)}
                  {s.class && <span className="muted" style={{ marginLeft: 8 }}>· {s.class.label}</span>}
                </span>
                <span className="muted" style={{ fontSize: 11 }}>ID {s.id}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Главная модалка                                                    */
/* ------------------------------------------------------------------ */
export default function PaymentCreateModal({ onClose, onCreated, initialStudent = null, initialYearStudyId = null }) {
  const [student, setStudent] = useState(initialStudent);

  // Контекст (годы + договоры) подгружается под выбранного ученика
  const [ctx, setCtx]         = useState(null);
  const [ctxLoading, setCtxL] = useState(false);
  const [yearId, setYearId]   = useState('');

  // Список позиций: каждая = { _key, serviceKey, agreementId, kind, name, price, qty }
  // serviceKey ('-' = «Произвольная позиция») задаёт agreementId/kind/name/price.
  // _key — стабильный React-ключ для строк (не отправляется на бэк).
  const [items, setItems] = useState([]);

  // Шапка платежа. Дефолтный канал — Модулькасса (самый частый сценарий:
  // пришли в офис, заплатили на кассе).
  const [provider, setProvider]       = useState('modulkassa');
  const [payerName, setPayerName]     = useState('');
  const [payerPhone, setPayerPhone]   = useState('');
  const [payerEmail, setPayerEmail]   = useState('');
  // Флажок «пользователь руками поправил поля плательщика» — после этого
  // мы перестаём перезатирать их подписантом при смене договора/года.
  const [payerEdited, setPayerEdited] = useState(false);

  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);

  // Lock body + Esc
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, busy]);

  // Загрузка контекста при выборе ученика
  useEffect(() => {
    if (!student) {
      setCtx(null); setYearId(''); setItems([]);
      return;
    }
    let cancelled = false;
    setCtxL(true); setError(null);
    api.get('/api/payments/context?student_id=' + student.id)
      .then((res) => {
        if (cancelled) return;
        setCtx(res);
        // Приоритет: явно переданный initialYearStudyId → дефолт из контекста.
        const preset = initialYearStudyId
          && (res.years || []).some((y) => y.id === Number(initialYearStudyId))
          ? String(initialYearStudyId)
          : (res.defaultYearId ? String(res.defaultYearId) : '');
        setYearId(preset);
        setItems([]);
      })
      .catch((e) => {
        if (cancelled) return;
        setCtx({ years: [], agreements: [], defaultYearId: null });
        setError(e instanceof ApiError ? e.message : 'Не удалось загрузить данные ученика');
      })
      .finally(() => { if (!cancelled) setCtxL(false); });
    return () => { cancelled = true; };
  }, [student?.id]);

  // Договоры по выбранному году (только не расторгнутые на сегодня)
  const yearAgreements = useMemo(() => {
    if (!ctx || !yearId) return [];
    const today = new Date().toISOString().slice(0, 10);
    const y = Number(yearId);
    return (ctx.agreements || []).filter((a) =>
      a.yearStudyId === y && (!a.dateTermination || a.dateTermination > today)
    );
  }, [ctx, yearId]);

  const mainAgreement  = yearAgreements.find((a) => a.type === 'school' || a.type === 'kindergarten') || null;
  const extrasAgreement = yearAgreements.find((a) => a.type === 'paid_services') || null;

  // Подписант: с основного договора, если есть; иначе с договора доп. услуг.
  const signatory = (mainAgreement && mainAgreement.signatory)
    || (extrasAgreement && extrasAgreement.signatory)
    || null;

  // Подтягиваем плательщика из подписанта при первом отображении / смене
  // ученика-года. Если пользователь начал править руками (payerEdited),
  // больше не перетираем.
  useEffect(() => {
    if (payerEdited) return;
    setPayerName(signatory?.name  || '');
    setPayerPhone(signatory?.phone || '');
    setPayerEmail(signatory?.email || '');
  }, [signatory?.name, signatory?.phone, signatory?.email, payerEdited]);

  // При смене ученика — снимаем «руками правил» (новый ученик = чистый старт).
  useEffect(() => { setPayerEdited(false); }, [student?.id]);

  // Можно ли вообще что-то заплатить на этот год
  const hasOnecLinkedAgr = yearAgreements.some((a) => a.onecLinked);

  // Список доступных услуг (для optgroup)
  const services = useMemo(() => {
    const out = { main: [], extras: [] };
    if (mainAgreement && mainAgreement.onecLinked) {
      const isSchool = mainAgreement.type === 'school';
      const monthlyKind  = isSchool ? 'school_monthly' : 'kg_monthly';
      const admissionKind = isSchool ? 'school_admission_fee' : 'kg_admission_fee';
      const typeLabel = AGREEMENT_TYPE_LABELS[mainAgreement.type] || mainAgreement.type;

      if (mainAgreement.tariffPrice) {
        out.main.push({
          key:          `${mainAgreement.id}:${monthlyKind}`,
          agreementId:  mainAgreement.id,
          kind:         monthlyKind,
          name:         `Ежемесячная оплата (${typeLabel}${mainAgreement.tariffName ? ' · ' + mainAgreement.tariffName : ''})`,
          unitPrice:    mainAgreement.tariffPrice,
          label:        `Ежемесячная оплата · ${fmtAmount(mainAgreement.tariffPrice)}`,
        });
      }
      // Вступительный взнос показываем, пока есть неоплаченный остаток.
      // unitPrice = остаток (взнос минус уже оплаченное), чтобы при частичной
      // оплате предлагалась именно доплата, а не полная сумма заново.
      const admPaid = mainAgreement.admissionFeePaidAmount || 0;
      const admRemaining = mainAgreement.admissionFee
        ? Math.max(0, mainAgreement.admissionFee - admPaid)
        : 0;
      if (admRemaining > 0.005) {
        const isPartial = admPaid > 0.005;
        out.main.push({
          key:         `${mainAgreement.id}:${admissionKind}`,
          agreementId: mainAgreement.id,
          kind:        admissionKind,
          name:        `Вступительный взнос (${typeLabel})${isPartial ? ' · остаток' : ''}`,
          unitPrice:   admRemaining,
          label:       isPartial
            ? `Вступительный взнос · остаток ${fmtAmount(admRemaining)}`
            : `Вступительный взнос · ${fmtAmount(admRemaining)}`,
        });
      }
    }
    if (extrasAgreement && extrasAgreement.onecLinked && Array.isArray(ctx?.extraLessons)) {
      // Бэк уже свернул в группы (subject + type + price). Учителя — только
      // для подсказки на hover, в позицию не идут. external_ref ставим как
      // младший lesson_id из группы — стабильный «представитель».
      for (const g of ctx.extraLessons) {
        const typeLabel = g.type === 'group' ? 'групп.' : 'инд.';
        const repId = (g.lessonIds && g.lessonIds[0]) || null;
        const teachersHint = (g.teacherNames || []).join(', ');
        out.extras.push({
          key:          `${extrasAgreement.id}:extra_lesson:${g.subjectId}:${g.type}:${g.priceStudent}`,
          agreementId:  extrasAgreement.id,
          kind:         'extra_lesson',
          externalRef:  repId ? String(repId) : null,
          name:         `${g.subjectName} (${typeLabel})`,
          unitPrice:    g.priceStudent || 0,
          label:        `${g.subjectName} (${typeLabel}) · ${fmtAmount(g.priceStudent)}`,
          teachersHint,
        });
      }
    }
    return out;
  }, [mainAgreement, extrasAgreement, ctx]);

  const allServices = useMemo(
    () => [...services.main, ...services.extras.filter((s) => !s.disabled)],
    [services]
  );

  // Когда есть хотя бы одна услуга и список позиций пуст — кладём первую
  // позицию по умолчанию (по первой услуге). Чтобы пользователь не нажимал
  // лишний раз «+ Добавить» в самом частом сценарии «одна позиция».
  useEffect(() => {
    if (items.length === 0 && allServices.length > 0) {
      const s = allServices[0];
      setItems([{
        _key:        randomKey(),
        serviceKey:  s.key,
        agreementId: s.agreementId,
        kind:        s.kind,
        name:        s.name,
        externalRef: s.externalRef || null,
        price:       s.unitPrice ? String(s.unitPrice) : '',
        qty:         '1',
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allServices.length === 0 ? null : allServices[0]?.key]);

  // Под текущий provider разрешены отрицательные количества/суммы (для
  // проводки «Корректировка 1С» — минус-возврат с сохранением структуры).
  const allowNegative = provider === 'onec_correction';

  // Применить выбранную услугу к позиции — подменить agreementId/kind/имя/цену.
  // «Произвольная позиция» больше не поддерживается: всё, что покупаем,
  // должно быть в справочнике (тариф/взнос/доп. занятие).
  const applyServiceToItem = (idx, sk) => {
    const s = allServices.find((x) => x.key === sk);
    if (!s) return;
    setItems((arr) => arr.map((it, i) => i !== idx ? it : ({
      ...it,
      serviceKey:  s.key,
      agreementId: s.agreementId,
      kind:        s.kind,
      name:        s.name,
      externalRef: s.externalRef || null,
      price:       s.unitPrice ? String(s.unitPrice) : it.price,
    })));
  };

  const updateItem = (idx, patch) => setItems((arr) => arr.map((it, i) =>
    i === idx ? { ...it, ...patch } : it));

  const addItem = () => {
    const s = allServices[0];
    if (!s) return; // не должно случиться — кнопка «+ Добавить» disabled, если нет услуг
    setItems((arr) => [...arr, {
      _key:        randomKey(),
      serviceKey:  s.key,
      agreementId: s.agreementId,
      kind:        s.kind,
      name:        s.name,
      externalRef: s.externalRef || null,
      price:       s.unitPrice ? String(s.unitPrice) : '',
      qty:         '1',
    }]);
  };

  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  // Итоговая сумма, посчитанная на лету (для отображения в шапке).
  const totalAmount = useMemo(() => items.reduce((s, it) => {
    const p = parseFloat(String(it.price || '0').replace(',', '.'));
    const q = parseFloat(String(it.qty   || '0').replace(',', '.'));
    return isFinite(p) && isFinite(q) ? s + p * q : s;
  }, 0), [items]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!student) { setError('Выберите ученика'); return; }
    if (!yearId)  { setError('Выберите учебный год'); return; }
    if (yearAgreements.length === 0) {
      setError('На выбранный год у ученика нет договоров');
      return;
    }
    if (!hasOnecLinkedAgr) {
      setError('Платёж нельзя создать: ни один из договоров на этот год не привязан к 1С');
      return;
    }
    if (items.length === 0) { setError('Добавьте хотя бы одну позицию'); return; }

    const payloadItems = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const price = parseFloat(String(it.price || '0').replace(',', '.'));
      const qty   = parseFloat(String(it.qty   || '1').replace(',', '.'));
      if (!it.name.trim()) {
        setError(`Позиция ${i + 1}: укажите название`); return;
      }
      if (!isFinite(price)) { setError(`Позиция ${i + 1}: некорректная цена`); return; }
      if (!isFinite(qty))   { setError(`Позиция ${i + 1}: некорректное количество`); return; }
      // Цена > 0 всегда. Количество — для onec_correction может быть отрицательным,
      // для остальных — только положительное.
      if (price <= 0) { setError(`Позиция ${i + 1}: цена должна быть положительной`); return; }
      if (qty === 0)  { setError(`Позиция ${i + 1}: количество не может быть 0`); return; }
      if (!allowNegative && qty < 0) {
        setError(`Позиция ${i + 1}: отрицательное количество допустимо только для «Корректировки 1С»`);
        return;
      }
      payloadItems.push({
        agreement_id: it.agreementId,
        kind:         it.kind || 'manual',
        name:         it.name.trim(),
        unit_price:   price,
        quantity:     qty,
        amount:       +(price * qty).toFixed(2),
        external_ref: it.externalRef || null,
      });
    }

    setBusy(true);
    try {
      // agreementId шапки — берём из первой позиции (для обратной совместимости с
      // существующими отчётами, которые иногда фильтруют payments по agreement_id).
      const headAgreement = payloadItems[0]?.agreement_id || null;
      const res = await api.post('/api/payments', {
        studentId:   student.id,
        agreementId: headAgreement,
        provider,
        payerName,
        payerPhone,
        payerEmail,
        items: payloadItems,
      });
      onCreated?.(res.payment.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось создать');
    } finally {
      setBusy(false);
    }
  };

  // Состояние «нечего создавать» для текущего года
  const blockerText = (() => {
    if (!student || ctxLoading) return null;
    if (!yearId) return null;
    if (yearAgreements.length === 0) return 'На выбранный год у ученика нет договоров.';
    if (!hasOnecLinkedAgr) {
      return 'Чтобы создать платёж, договор должен быть привязан к 1С. Откройте карточку ученика → договор → «Связь с 1С».';
    }
    if (allServices.length === 0) {
      return 'По привязанным к 1С договорам нет доступных услуг для оплаты.';
    }
    return null;
  })();

  const submitDisabled = busy || !student || !yearId || !!blockerText || items.length === 0;

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay') && !busy) onClose(); }}>
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>Новый платёж</h2>
          <button className="sd-close" onClick={onClose} disabled={busy}><Icon name="x" size={14} /></button>
        </div>

        <form className="sd-edit__form" onSubmit={submit} noValidate>
          <div className="sd-edit__body">
            {/* Ученик */}
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Ученик *</label>
              <StudentSelect value={student} onChange={setStudent} disabled={busy} />
            </div>

            {/* Учебный год */}
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Учебный год *</label>
              <select
                className="sd-edit__select"
                value={yearId}
                onChange={(e) => setYearId(e.target.value)}
                disabled={!student || ctxLoading}
              >
                {!student && <option value="">— выберите ученика —</option>}
                {student && ctxLoading && <option value="">Загрузка…</option>}
                {student && !ctxLoading && (ctx?.years || []).length === 0 && (
                  <option value="">Нет учебных годов</option>
                )}
                {student && !ctxLoading && (ctx?.years || []).map((y) => {
                  const today = new Date().toISOString().slice(0, 10);
                  const has = (ctx.agreements || []).some((a) => a.yearStudyId === y.id
                    && (!a.dateTermination || a.dateTermination > today));
                  return (
                    <option key={y.id} value={y.id}>
                      {y.name}{has ? '' : ' — без договора'}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Договоры на выбранный год — авто-привязка, info-блок */}
            {student && !ctxLoading && yearId && (
              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label">
                  Договоры на этот год
                  <span style={{ color: 'var(--fg-faint)', fontWeight: 400, marginLeft: 6 }}>
                    (привязка автоматическая)
                  </span>
                </label>
                {yearAgreements.length === 0 ? (
                  <div className="pay-warn">
                    На выбранный год у ученика нет договоров.
                  </div>
                ) : (
                  <div className="pay-agr-list">
                    {yearAgreements.map((a) => (
                      <div
                        key={a.id}
                        className={'pay-agr pay-agr--info'
                          + (a.onecLinked ? '' : ' pay-agr--no1c')}
                      >
                        <div className="pay-agr__main">
                          <div className="pay-agr__title">
                            <span className="agr-row__type-pill">{AGREEMENT_TYPE_LABELS[a.type] || a.type}</span>
                            <span className="pay-agr__num">№ {a.displayNumber}</span>
                            {a.onecLinked
                              ? <span className="pay-agr__onec is-on">1С: {a.onecCode || '—'}</span>
                              : <span className="pay-agr__onec is-off">не привязан к 1С</span>}
                          </div>
                          <div className="pay-agr__meta">
                            {a.tariffName && <span>{a.tariffName}</span>}
                            {a.tariffPrice ? <span> · {fmtAmount(a.tariffPrice)}/мес</span> : null}
                            {a.admissionFee
                              ? <span> · взнос {fmtAmount(a.admissionFee)}{
                                  a.admissionFeePaid
                                    ? ' (уплачен)'
                                    : (a.admissionFeePaidAmount > 0.005
                                        ? ` (оплачено ${fmtAmount(a.admissionFeePaidAmount)})`
                                        : '')
                                }</span>
                              : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {blockerText && <div className="pay-warn" style={{ marginTop: 8 }}>{blockerText}</div>}
              </div>
            )}

            {/* Канал оплаты */}
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Канал оплаты *</label>
              <select className="sd-edit__select" value={provider} onChange={(e) => setProvider(e.target.value)}>
                {PROVIDERS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
              </select>
            </div>

            {/* Плательщик. Подтягивается из подписанта основного договора
                (или paid_services, если основного нет). После ручной правки —
                перетирать не будем. */}
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">ФИО плательщика</label>
              <input
                className="sd-edit__input"
                placeholder="из подписанта договора"
                value={payerName}
                onChange={(e) => { setPayerName(e.target.value); setPayerEdited(true); }}
              />
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Телефон</label>
              <input
                className="sd-edit__input"
                placeholder="+7…"
                value={payerPhone}
                onChange={(e) => { setPayerPhone(e.target.value); setPayerEdited(true); }}
              />
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Email</label>
              <input
                className="sd-edit__input"
                type="email"
                placeholder="email@example.com"
                value={payerEmail}
                onChange={(e) => { setPayerEmail(e.target.value); setPayerEdited(true); }}
              />
            </div>

            {/* Позиции платежа — список с кнопками «удалить» и «+ добавить». */}
            {student && !ctxLoading && yearId && !blockerText && (
              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  Позиции
                  {allowNegative && (
                    <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}>
                      · для «Корректировки 1С» количество может быть отрицательным
                    </span>
                  )}
                </label>

                {items.map((it, idx) => (
                  <div key={it._key} className="sd-edit__inline-block" style={{ marginBottom: 6 }}>
                    <div className="sd-edit__inline-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span>Позиция {idx + 1}</span>
                      {items.length > 1 && (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => removeItem(idx)}
                          title="Удалить позицию"
                          style={{ padding: '0 6px', height: 22 }}
                        >
                          <Icon name="x" size={11} /> Удалить
                        </button>
                      )}
                    </div>

                    <div className="sd-edit__field sd-edit__field--full">
                      <label className="sd-edit__label">Услуга</label>
                      <select
                        className="sd-edit__select"
                        value={it.serviceKey || ''}
                        onChange={(e) => applyServiceToItem(idx, e.target.value)}
                      >
                        {services.main.length > 0 && (
                          <optgroup label="Основная услуга">
                            {services.main.map((s) => (
                              <option key={s.key} value={s.key}>{s.label}</option>
                            ))}
                          </optgroup>
                        )}
                        {services.extras.length > 0 && (
                          <optgroup label="Дополнительные занятия">
                            {services.extras.map((s) => (
                              <option
                                key={s.key}
                                value={s.key}
                                disabled={s.disabled}
                                title={s.teachersHint ? 'Ведут: ' + s.teachersHint : undefined}
                              >
                                {s.label}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </div>

                    <div className="sd-edit__field">
                      <label className="sd-edit__label">Цена за ед., ₽ *</label>
                      <input
                        className="sd-edit__input"
                        type="number"
                        step="0.01"
                        min="0"
                        value={it.price}
                        onChange={(e) => updateItem(idx, { price: e.target.value })}
                      />
                    </div>
                    <div className="sd-edit__field">
                      <label className="sd-edit__label">
                        Количество *
                        {allowNegative && <span className="muted" style={{ fontSize: 10, marginLeft: 4 }}>(можно «−»)</span>}
                      </label>
                      <input
                        className="sd-edit__input"
                        type="number"
                        step="1"
                        // Снимаем min, если это корректировка 1С — иначе min=0 не пускает «-1».
                        {...(allowNegative ? {} : { min: '0' })}
                        value={it.qty}
                        onChange={(e) => updateItem(idx, { qty: e.target.value })}
                      />
                    </div>

                    {(() => {
                      const p = parseFloat(String(it.price || '0').replace(',', '.'));
                      const q = parseFloat(String(it.qty   || '0').replace(',', '.'));
                      if (!isFinite(p) || !isFinite(q) || q === 0) return null;
                      const sum = +(p * q).toFixed(2);
                      return (
                        <div className="sd-edit__field sd-edit__field--full" style={{ textAlign: 'right' }}>
                          <span className="muted" style={{ fontSize: 12 }}>Сумма по позиции:</span>{' '}
                          <span className="mono" style={{ color: sum < 0 ? 'var(--red-fg)' : 'var(--fg)' }}>
                            {fmtAmount(sum)}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                ))}

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 4 }}>
                  <button
                    type="button"
                    className="btn btn--sm"
                    onClick={addItem}
                    disabled={allServices.length === 0}
                  >
                    <Icon name="plus" size={12} /> Добавить позицию
                  </button>
                  {items.length > 0 && (
                    <span style={{ fontSize: 13 }}>
                      Итого: <b className="mono" style={{ color: totalAmount < 0 ? 'var(--red-fg)' : 'var(--fg)' }}>
                        {fmtAmount(totalAmount)}
                      </b>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose} disabled={busy}>Отмена</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={submitDisabled}>
              {busy ? 'Создаём…' : 'Создать черновик'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
